import type { DifficultyId } from "./difficulty";
import type { StageId } from "./stages";
import type { VehicleId } from "./vehicles";

export type CloudSave = {
  playerId: string;
  appleUserId: string | null;
  playerName: string;
  coins: number;
  vehiclesOwned: VehicleId[];
  equippedVehicle: VehicleId;
  stagesOwned: StageId[];
  equippedStage: StageId;
  removeAds: boolean;
  starterOwned: boolean;
  bestByDifficulty: Record<DifficultyId, number>;
  gamesPlayed: number;
  runsSinceAd: number;
  difficulty: DifficultyId;
  welcomeGiftClaimed: boolean;
  nameChosen: boolean;
  powerAuto: boolean;
  ghostOn: boolean;
  streakCount: number;
  streakLastDay: string;
  streakClaimedDay: string;
  dailyDay: string;
  dailyProgress: Record<string, number>;
  dailyClaimed: string[];
  ghostByDifficulty: Record<string, unknown>;
};

function uniq<T extends string>(a: T[], b: T[]): T[] {
  return [...new Set([...a, ...b])];
}

function maxBest(
  a: Record<DifficultyId, number>,
  b: Record<DifficultyId, number>,
): Record<DifficultyId, number> {
  return {
    easy: Math.max(a.easy ?? 0, b.easy ?? 0),
    medium: Math.max(a.medium ?? 0, b.medium ?? 0),
    hard: Math.max(a.hard ?? 0, b.hard ?? 0),
  };
}

/** Fresh server row — local device still holds the real save. */
export function isCloudEmpty(remote: CloudSave): boolean {
  const best = Math.max(
    remote.bestByDifficulty.easy ?? 0,
    remote.bestByDifficulty.medium ?? 0,
    remote.bestByDifficulty.hard ?? 0,
  );
  return (
    remote.coins <= 40 &&
    !remote.removeAds &&
    !remote.starterOwned &&
    remote.vehiclesOwned.length <= 1 &&
    remote.stagesOwned.length <= 1 &&
    best === 0 &&
    remote.gamesPlayed <= 1
  );
}

export function localRicher(local: CloudSave, remote: CloudSave): boolean {
  const lb = Math.max(
    local.bestByDifficulty.easy ?? 0,
    local.bestByDifficulty.medium ?? 0,
    local.bestByDifficulty.hard ?? 0,
  );
  const rb = Math.max(
    remote.bestByDifficulty.easy ?? 0,
    remote.bestByDifficulty.medium ?? 0,
    remote.bestByDifficulty.hard ?? 0,
  );
  return (
    local.coins > remote.coins ||
    local.vehiclesOwned.length > remote.vehiclesOwned.length ||
    local.stagesOwned.length > remote.stagesOwned.length ||
    (local.removeAds && !remote.removeAds) ||
    (local.starterOwned && !remote.starterOwned) ||
    lb > rb
  );
}

/** Union entitlements, keep the better progress. Used on first pull / migrate. */
export function mergeCloud(local: CloudSave, remote: CloudSave): CloudSave {
  const vehiclesOwned = uniq(local.vehiclesOwned, remote.vehiclesOwned);
  const stagesOwned = uniq(local.stagesOwned, remote.stagesOwned);
  const equippedVehicle = vehiclesOwned.includes(local.equippedVehicle)
    ? local.equippedVehicle
    : remote.equippedVehicle;
  const equippedStage = stagesOwned.includes(local.equippedStage)
    ? local.equippedStage
    : remote.equippedStage;
  const appleUserId = local.appleUserId || remote.appleUserId;
  const playerId = appleUserId || local.playerId || remote.playerId;
  const dailyDay =
    local.dailyDay >= remote.dailyDay ? local.dailyDay : remote.dailyDay;
  const dailyProgress =
    dailyDay === local.dailyDay && dailyDay === remote.dailyDay
      ? Object.fromEntries(
          [...new Set([...Object.keys(local.dailyProgress), ...Object.keys(remote.dailyProgress)])].map(
            (k) => [
              k,
              Math.max(local.dailyProgress[k] ?? 0, remote.dailyProgress[k] ?? 0),
            ],
          ),
        )
      : dailyDay === local.dailyDay
        ? local.dailyProgress
        : remote.dailyProgress;
  const dailyClaimed =
    dailyDay === local.dailyDay && dailyDay === remote.dailyDay
      ? uniq(local.dailyClaimed, remote.dailyClaimed)
      : dailyDay === local.dailyDay
        ? local.dailyClaimed
        : remote.dailyClaimed;

  return {
    ...remote,
    ...local,
    playerId,
    appleUserId,
    playerName: local.playerName.trim() || remote.playerName.trim() || "Courier",
    coins: Math.max(local.coins, remote.coins),
    vehiclesOwned,
    stagesOwned,
    equippedVehicle,
    equippedStage,
    removeAds: local.removeAds || remote.removeAds,
    starterOwned: local.starterOwned || remote.starterOwned,
    bestByDifficulty: maxBest(local.bestByDifficulty, remote.bestByDifficulty),
    gamesPlayed: Math.max(local.gamesPlayed, remote.gamesPlayed),
    welcomeGiftClaimed: local.welcomeGiftClaimed || remote.welcomeGiftClaimed,
    nameChosen: local.nameChosen || remote.nameChosen,
    dailyDay,
    dailyProgress,
    dailyClaimed,
    streakCount: Math.max(local.streakCount, remote.streakCount),
    ghostByDifficulty: {
      ...remote.ghostByDifficulty,
      ...local.ghostByDifficulty,
    },
  };
}
